import { CSEService } from "@/service/CSEService";
import { BookingFunnelService } from "@/service/BookingFunnelService/BookingFunnelService";
import store from "@/store/BookingStore";
import { Option, PrestType, Traveller } from "../../../../requests-module/src/booking-state/BookingV2";
import useBookingFunnelCSEConfig from "@/composables/useBookingFunnelCSEConfig";
import { isCSE } from "@/utils/cse";
import type { Locale } from "@/service/translate/flights/flightTranslations.types";

const participantsSyncCache = new Map<string, string>();
const insuranceSyncCache = new Set<string>();

const buildParticipantsPayloadFromCookie = (): Traveller[] | null => {
    const booking = store.getters.getBooking;
    if (!booking?.travellers?.length) return null;

    const cseService = new CSEService(store.getters.getLocale || "");
    let cookieTravellers: any[] = [];
    try {
        cookieTravellers = cseService.extractCSETravellerData().travalers || [];
    } catch (error) {
        console.error(
            "[applySingleCompanyConfigOnOptionsInit] failed to read travelerCompanions cookie",
            error
        );
        return null;
    }
    if (!cookieTravellers.length) return null;

    const selectedCookieTravellers = cookieTravellers.filter(
        (traveller) => traveller.isSelected !== false
    );
    if (!selectedCookieTravellers.length) return null;

    const payload = booking.travellers.map((traveller, index) => {
        const cookieTraveller = selectedCookieTravellers[index];
        if (!cookieTraveller) return traveller;
        return {
            ...traveller,
            firstName: cookieTraveller.fisrtName || traveller.firstName,
            lastName: cookieTraveller.lastName || traveller.lastName,
            fulfilled: true,
        };
    });

    return payload;
};

const syncMandatoryParticipants = async (): Promise<void> => {
    const booking = store.getters.getBooking;
    const token = booking?.boReference || store.getters.getToken;
    if (!token || !booking?.travellers?.length) return;

    const participantsPayload = buildParticipantsPayloadFromCookie();
    if (!participantsPayload) return;

    const signature = JSON.stringify(
        participantsPayload.map((traveller) => ({
            id: traveller.id,
            firstName: traveller.firstName,
            lastName: traveller.lastName,
        }))
    );

    if (participantsSyncCache.get(token) === signature) return;

    const hasDiff = booking.travellers.some((traveller, index) => {
        const mapped = participantsPayload[index];
        return (
            traveller.firstName !== mapped.firstName ||
            traveller.lastName !== mapped.lastName
        );
    });
    if (!hasDiff) {
        participantsSyncCache.set(token, signature);
        return;
    }

    console.log("[applySingleCompanyConfigOnOptionsInit] updating participants", participantsPayload);
    await BookingFunnelService.updateParticipants(participantsPayload);
    participantsSyncCache.set(token, signature);
};

const buildMandatoryInsuranceOptions = (): Option[] => {
    const booking = store.getters.getBooking;
    if (!booking?.options?.length) return [];

    const updatedOptions: Option[] = [];

    const globalInsurance = booking.options.find(
        (option) => option.prestTypeId === PrestType.Insurance && !option.nominative
    );
    if (globalInsurance && globalInsurance.quantity !== 1) {
        updatedOptions.push({ ...globalInsurance, quantity: 1 });
    }

    const nominativeInsurances = booking.options.filter(
        (option) => option.prestTypeId === PrestType.Insurance && option.nominative
    );
    if (nominativeInsurances.length) {
        const primaryInsuranceId = nominativeInsurances[0].id;
        nominativeInsurances.forEach((option) => {
            const shouldSelectOption = option.id === primaryInsuranceId;
            const nextPricePerTraveller = (option.pricePerTraveller || []).map((pt) => ({
                ...pt,
                selected: shouldSelectOption ? true : false,
            }));
            const nextQuantity = nextPricePerTraveller.filter((pt) => pt.selected).length;
            const currentQuantity = option.quantity || 0;
            const hasPriceTravellerDiff = (option.pricePerTraveller || []).some(
                (pt, index) => pt.selected !== nextPricePerTraveller[index]?.selected
            );
            if (hasPriceTravellerDiff || currentQuantity !== nextQuantity) {
                updatedOptions.push({
                    ...option,
                    pricePerTraveller: nextPricePerTraveller,
                    quantity: nextQuantity,
                });
            }
        });
    }

    return updatedOptions;
};

const syncMandatoryInsurance = async (): Promise<void> => {
    const booking = store.getters.getBooking;
    const token = booking?.boReference || store.getters.getToken;
    if (!token || insuranceSyncCache.has(token)) return;

    const optionsToUpdate = buildMandatoryInsuranceOptions();
    if (!optionsToUpdate.length) {
        insuranceSyncCache.add(token);
        return;
    }

    await BookingFunnelService.updateServices(optionsToUpdate, "service");
    insuranceSyncCache.add(token);
};

export const applySingleCompanyConfigOnOptionsInit = async (): Promise<void> => {
    const { isMandatoryParticipants, isMandatoryInsurance } = useBookingFunnelCSEConfig();
    const locale = store.getters.getLocale;
    const isCSEContext = !!locale && isCSE(locale as Locale);

    if (isCSEContext) {
        await syncMandatoryParticipants();
    }
    if (isMandatoryInsurance.value) {
        await syncMandatoryInsurance();
    }
};

